import { Component, type ReactNode } from "react";

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log for ops; survives reload via localStorage
    try {
      const log = JSON.parse(
        localStorage.getItem("bp26.errors") ?? "[]",
      ) as unknown[];
      log.unshift({
        t: Date.now(),
        msg: error.message,
        stack: error.stack?.slice(0, 1500),
        componentStack: info.componentStack?.slice(0, 800),
      });
      localStorage.setItem("bp26.errors", JSON.stringify(log.slice(0, 20)));
    } catch {
      /* ignore */
    }
  }

  reset = () => this.setState({ error: null });

  reload = () => {
    location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-full flex flex-col items-center justify-center p-8 text-center safe-top safe-bottom">
          <div className="w-12 h-12 rounded-full bg-risk/15 text-risk text-2xl flex items-center justify-center mb-4">
            !
          </div>
          <h1 className="text-zinc-100 text-xl font-bold mb-2">
            Er ging iets mis
          </h1>
          <p className="text-zinc-400 text-sm max-w-sm mb-1">
            De app crashte met deze fout:
          </p>
          <p className="text-zinc-500 text-2xs font-mono max-w-sm mb-6 break-words">
            {this.state.error.message}
          </p>
          <div className="flex gap-2">
            <button
              onClick={this.reset}
              className="px-4 py-2.5 rounded-lg border border-line text-zinc-300 text-sm hover:bg-ink-700"
            >
              Probeer opnieuw
            </button>
            <button
              onClick={this.reload}
              className="px-4 py-2.5 rounded-lg bg-accent text-ink-950 font-semibold text-sm"
            >
              Herlaad app
            </button>
          </div>
          <p className="text-2xs text-zinc-600 mt-6 max-w-sm">
            Reeds gesyncte data staat veilig in Firestore. Een schrijfactie
            die exact tijdens de crash bezig was kan verloren zijn —
            controleer kritische items na herladen.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
